import { useState, useEffect, useRef } from 'react';
import { io, Socket } from 'socket.io-client';
import { useLanguageSync } from '../../hooks/useLanguageSync';
import { getServerUrl } from '../../lib/getServerUrl';

// Import types from shared package (relative path for now)
type TeamColor = 'BLUE' | 'RED' | 'GREEN' | 'YELLOW';

interface PollOption {
  id: string;
  text: string;
}

interface BondOption {
  id: string;
  text: string;
  resolvedText?: string;
}

export type AudienceState =
  | { type: 'standby' }
  | { type: 'sheet-vote'; voteId: string; canVote: boolean; hasAlreadyVoted?: boolean; votedForSheetId?: string }
  | { type: 'sheet-vote-results'; voteId: string }
  | { type: 'sheet-vote-winner'; sheetId: string; sheetName: string; isCelebration?: boolean }
  | { type: 'vote'; voteId: string; canVote: boolean }
  | { type: 'challenge'; challengeId: string; isMyTeam: boolean }
  | { type: 'result-display'; resultType: string; data: any }
  | { type: 'viewing-sheet'; characterId: string }
  | { type: 'poll-prepare'; pollId: string; targetTeam: TeamColor | null }
  | { type: 'poll-voting'; pollId: string; options: PollOption[]; remainingSeconds: number; endsAt: number; serverTime: number; canVote: boolean; hasAlreadyVoted?: boolean; votedOptionId?: string }
  | { type: 'poll-results'; pollId: string }
  | { type: 'poll-waiting'; message: string }
  | { type: 'bond-vote'; voteId: string; options: BondOption[]; canVote: boolean; hasAlreadyVoted?: boolean; votedOptionId?: string }
  | { type: 'bond-vote-waiting'; message: string }
  | { type: 'bond-vote-results'; voteId: string }
  | { type: 'bond-vote-winner'; voteId: string; fromTeam: TeamColor; heroName: string; heroImageUrl?: string; bondText: string }
  | { type: 'lottery-voting'; lotteryId: string; remainingSeconds: number; endsAt: number; serverTime: number; hasAlreadyVoted?: boolean; votedYes?: boolean; texts?: { votingQuestion?: string; buttonYes?: string; buttonNo?: string } }
  | { type: 'lottery-winner'; lotteryId: string; isWinner: boolean; texts?: { winnerTitle?: string; winnerSubtitle?: string } }
  | { type: 'challenge-active'; challengeId: string; variant: 'mole' | 'balance' | 'pixelart' | 'explore'; config: any; remainingSeconds: number; endsAt: number; serverTime: number; score: number; targetScore: number; whiteClicks?: number; blackClicks?: number; numColors?: number; userGrid?: number[][] | null }
  | { type: 'challenge-result'; challengeId: string; result: 'success' | 'failure'; variant: 'mole' | 'balance' | 'pixelart' | 'explore'; personalAccuracy?: number; averageAccuracy?: number }
  | { type: 'consiglio'; remainingSeconds: number; endsAt: number; serverTime: number; hasSubmitted?: boolean }
  | { type: 'battle'; battleId: string; battleState: any };

interface AudienceStateChangeEvent {
  state: AudienceState;
  timestamp: number;
}

/**
 * Estrae l'ID rilevante dallo stato per confronto intelligente.
 * Usato per evitare remount quando il sync periodico rimanda lo stesso stato.
 */
function getStateKey(state: AudienceState): string | null {
  switch (state.type) {
    case 'poll-voting': return state.pollId;
    case 'poll-prepare': return state.pollId;
    case 'poll-results': return state.pollId;
    case 'challenge-active': return state.challengeId;
    case 'challenge-result': return state.challengeId;
    case 'lottery-voting': return state.lotteryId;
    case 'lottery-winner': return state.lotteryId;
    case 'sheet-vote': return state.voteId;
    case 'sheet-vote-results': return state.voteId;
    case 'sheet-vote-winner': return state.sheetId;
    case 'bond-vote': return state.voteId;
    case 'bond-vote-results': return state.voteId;
    case 'bond-vote-winner': return state.voteId;
    case 'battle': return state.battleId;
    default: return null;
  }
}

function isInteractiveState(type: string): boolean {
  // Stati che NON devono interrompere la consultazione di una scheda eroe:
  // - standby/viewing-sheet: ovvio
  // - sheet-vote/results/winner: rendono la stessa StandbyScreen, l'utente deve poter navigare le schede
  const nonInteractive = ['standby', 'viewing-sheet', 'sheet-vote', 'sheet-vote-results', 'sheet-vote-winner'];
  return !nonInteractive.includes(type);
}

// Module-level socket instance for accessing from other components
let audienceSocket: Socket | null = null;

/**
 * Get the current audience socket instance
 * Returns null if not yet initialized
 */
export function getAudienceSocket(): Socket | null {
  return audienceSocket;
}

/**
 * Hook per sincronizzare lo stato audience con il server
 *
 * L'admin pilota completamente l'esperienza:
 * - Eventi server → cambio stato automatico
 * - Eventi interattivi hanno priorità su consultazione locale
 */
export function useAudienceState(teamColor?: string, overrides?: { sessionId?: string; audienceId?: string; nickname?: string; isSpectator?: boolean }) {
  const [socket, setSocket] = useState<Socket | null>(null);
  const [state, setState] = useState<AudienceState>({ type: 'standby' });
  const [previousState, setPreviousState] = useState<AudienceState>({ type: 'standby' });
  const [isConnected, setIsConnected] = useState(false);
  const [wasEverConnected, setWasEverConnected] = useState(false);
  const [stateVersion, setStateVersion] = useState(0);
  const [activeVotingTeam, setActiveVotingTeam] = useState<string | null>(null);
  const stateRef = useRef<AudienceState>(state);
  stateRef.current = state;
  const healthCheckTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Sync language changes from server
  useLanguageSync(socket, isConnected);

  useEffect(() => {
    // Connetti a Socket.IO namespace /audience (usa VITE_API_URL per Railway, altrimenti hostname:3000 per LAN)
    const serverUrl = getServerUrl('audience');
    const socketInstance = io(serverUrl, {
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionAttempts: Infinity,
      reconnectionDelay: 500,
      reconnectionDelayMax: 5000,
      timeout: 20000,
    });

    socketInstance.on('connect', () => {
      console.log('[AUDIENCE-STATE] Connected to /audience namespace');
      setIsConnected(true);
      setWasEverConnected(true);

      // Join audience room con sessionId, team e audienceId (per riconnessione)
      const sessionId = overrides?.sessionId || localStorage.getItem('sessionId');
      const audienceId = overrides?.audienceId || localStorage.getItem('audienceId');
      const nickname = overrides?.nickname || localStorage.getItem('nickname');

      console.log('[AUDIENCE-STATE] localStorage values:', { sessionId, audienceId, teamColor, nickname });

      if (sessionId && teamColor) {
        console.log('[AUDIENCE-STATE] Emitting audience:join...');
        socketInstance.emit('audience:join', {
          sessionId,
          team: teamColor,
          audienceId, // Permette riconnessione senza duplicati
          nickname,
        });
      } else {
        console.error('[AUDIENCE-STATE] Missing sessionId or teamColor, cannot join!');
      }
    });

    socketInstance.on('disconnect', () => {
      console.log('[AUDIENCE-STATE] Disconnected from server');
      setIsConnected(false);
    });

    // Gestisce risposta join con team potenzialmente diverso (anti-switch)
    socketInstance.on('audience:joined', (data: any) => {
      console.log('[AUDIENCE-STATE] Joined with data:', data);

      // In spectator mode, skip localStorage updates and reload
      if (overrides?.isSpectator) return;

      // Aggiorna localStorage con dati effettivi dal server
      if (data.audienceId) {
        localStorage.setItem('audienceId', data.audienceId);
      }
      if (data.team) {
        const currentTeam = localStorage.getItem('teamColor');
        if (currentTeam?.toLowerCase() !== data.team.toLowerCase()) {
          console.log(`[AUDIENCE-STATE] Team corrected by server: ${currentTeam} → ${data.team}`);
          localStorage.setItem('teamColor', data.team.toUpperCase());
          // Ricarica per applicare il team corretto alla UI
          window.location.reload();
        }
      }
    });

    // Gestisce errori dal server
    socketInstance.on('audience:error', (data: { message: string }) => {
      console.error('[AUDIENCE-STATE] Server error:', data.message);
      if (overrides?.isSpectator) return;
      // Se c'è un errore di join, pulisci localStorage e torna alla join screen
      localStorage.removeItem('audienceId');
      localStorage.removeItem('sessionId');
      localStorage.removeItem('teamColor');
      window.location.href = '/audience/join';
    });

    // Gestisce reset sessione da admin
    socketInstance.on('audience:session-reset', (data: any) => {
      console.log('[AUDIENCE-STATE] Session reset by admin:', data);
      if (overrides?.isSpectator) return;
      // Pulisci localStorage e torna alla join screen
      localStorage.removeItem('audienceId');
      localStorage.removeItem('sessionId');
      localStorage.removeItem('teamColor');
      window.location.href = '/audience/join';
    });

    // Listener principale: cambio stato
    socketInstance.on('audience:state-change', (event: AudienceStateChangeEvent) => {
      console.log('[AUDIENCE-STATE] State change:', event.state.type);

      // Cancella health-check timer (la connessione è viva)
      if (healthCheckTimerRef.current) {
        clearTimeout(healthCheckTimerRef.current);
        healthCheckTimerRef.current = null;
      }

      const newState = event.state;
      const currentState = stateRef.current;
      const isSameState = currentState.type === newState.type
        && getStateKey(currentState) === getStateKey(newState);

      // Incrementa stateVersion solo se lo stato è effettivamente diverso
      if (!isSameState) {
        setStateVersion(prev => prev + 1);
      }

      handleIncomingState(newState);
    });

    // Listener per cambio squadra attiva in votazione (modalita' SEQUENTIAL)
    socketInstance.on('audience:voting-team-changed', (data: { activeTeam: string | null }) => {
      console.log('[AUDIENCE-STATE] Voting team changed:', data.activeTeam);
      setActiveVotingTeam(data.activeTeam);
    });

    setSocket(socketInstance);
    if (!overrides?.isSpectator) {
      audienceSocket = socketInstance; // Store in module-level variable (skip for spectators)
    }

    return () => {
      socketInstance.disconnect();
      if (!overrides?.isSpectator) {
        audienceSocket = null; // Clean up on unmount
      }
    };
  }, [teamColor, overrides?.sessionId, overrides?.audienceId]);

  /**
   * Gestisce cambio stato con priorità.
   * Usa stateRef per evitare stale closure.
   */
  const handleIncomingState = (newState: AudienceState) => {
    const currentState = stateRef.current;

    // Se l'utente sta consultando una scheda personaggio (stato locale)
    if (currentState.type === 'viewing-sheet') {
      if (isInteractiveState(newState.type)) {
        // Eventi interattivi hanno priorità assoluta: esci dalla scheda
        console.log('[AUDIENCE-STATE] Interactive event overriding sheet view');
        window.history.back();
        setPreviousState(newState);
        setState(newState);
      } else {
        // Stato non-interattivo (es. standby): aggiorna solo previousState
        // per quando l'utente tornerà indietro, senza interrompere la consultazione
        setPreviousState(newState);
      }
      return;
    }

    // Salva lo stato corrente come previous (solo se non è viewing-sheet)
    if (newState.type !== 'viewing-sheet') {
      setPreviousState(newState);
    }

    setState(newState);
  };

  /**
   * Azione locale: visualizza scheda personaggio
   * (può essere interrotta da eventi server)
   */
  const viewCharacterSheet = (characterId: string) => {
    console.log('[AUDIENCE-STATE] Viewing character sheet:', characterId);
    // Push a history entry so browser back button returns to hero list
    // instead of navigating away to the join page
    window.history.pushState({ viewingSheet: true }, '');
    setState({
      type: 'viewing-sheet',
      characterId,
    });
  };

  /**
   * Torna allo stato precedente (o standby se non c'era uno stato precedente)
   * Called by the in-app "Tutti gli eroi" button — also pops the history entry
   */
  const returnToPreviousState = () => {
    console.log('[AUDIENCE-STATE] Returning to previous state:', previousState.type);
    setState(prev => {
      if (prev.type === 'viewing-sheet') {
        // Pop the history entry we pushed when entering sheet view.
        // This will fire popstate, but since state is already changed,
        // the popstate handler will be a no-op.
        window.history.back();
      }
      return previousState;
    });
  };

  // Intercept browser back button when viewing a character sheet
  useEffect(() => {
    const handlePopState = () => {
      setState(prev => {
        if (prev.type === 'viewing-sheet') {
          console.log('[AUDIENCE-STATE] Browser back pressed, returning to previous state');
          return previousState;
        }
        return prev;
      });
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [previousState]);

  // Sync universale: periodico + visibility change + health-check
  useEffect(() => {
    if (!socket || !isConnected) return;

    const doSync = () => {
      // Cancella eventuale health-check precedente ancora pendente
      if (healthCheckTimerRef.current) {
        clearTimeout(healthCheckTimerRef.current);
      }
      socket.emit('audience:request-state');
      // Health-check: se non arriva risposta in 3s, forza reconnect
      healthCheckTimerRef.current = setTimeout(() => {
        console.log('[AUDIENCE-STATE] Health-check timeout, forcing reconnect');
        healthCheckTimerRef.current = null;
        socket.disconnect().connect();
      }, 3000);
    };

    // Sync periodico (ogni 5s, in QUALSIASI stato)
    const interval = setInterval(doSync, 5000);

    // Sync immediato al risveglio schermo
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        console.log('[AUDIENCE-STATE] Screen woke up, syncing state');
        doSync();
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      clearInterval(interval);
      if (healthCheckTimerRef.current) {
        clearTimeout(healthCheckTimerRef.current);
        healthCheckTimerRef.current = null;
      }
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [socket, isConnected]);

  // Check if user's team can vote in SEQUENTIAL mode
  const canVoteInSequential = activeVotingTeam === null || activeVotingTeam === teamColor?.toUpperCase();

  return {
    socket,
    state,
    previousState,
    isConnected,
    wasEverConnected,
    stateVersion,
    viewCharacterSheet,
    returnToPreviousState,
    activeVotingTeam,
    canVoteInSequential,
  };
}
